import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fetchPageCopy } from '../lib/copy';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';

const HOME_DEFAULTS = {
  hero_headline:      'Your creative partner for commercial film\nand content.',
  hero_subtext:       'End-to-end creative production and film editing for brands and agencies.',
  statement_headline: 'From script to delivery, films built to perform.',
  statement_body:     'Drawing on years of experience within an award-winning film production company and a global media agency, the studio brings together world-class craft with a deep understanding of audiences, channels, and what makes content truly land.',
  section_work:       'Selected Work',
  section_brands:     "Brands I've Worked With",
};

export default function Home() {
  const [photos, setPhotos]         = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [crops, setCrops]           = useState({});
  const [brandCrops, setBrandCrops] = useState({});
  const [brands, setBrands]         = useState([]);
  const [work, setWork]             = useState([]);
  const [copy, setCopy]             = useState(null);
  const isMobile                    = useIsMobile();
  usePageTitle(null);

  useEffect(() => {
    async function load() {
      // Featured photos for hero
      let { data: featured, error: featuredErr } = await supabase.from('photos').select('id, url').eq('featured', true).limit(6);
      if (featuredErr) console.error('[Home] featured photos error:', featuredErr);
      if (!featured || featured.length === 0) {
        const { data: all, error: allErr } = await supabase.from('photos').select('id, url').limit(6);
        if (allErr) console.error('[Home] all photos error:', allErr);
        featured = all;
      }
      setPhotos(featured || []);

      // Brand logos
      const { data: brandData, error: brandsErr } = await supabase.from('portfolio_brands').select('*').order('display_order').limit(12);
      if (brandsErr) console.error('[Home] brands error:', brandsErr);
      setBrands(brandData || []);

      // Selected work
      const { data: videos, error: videosErr } = await supabase.from('portfolio_videos').select('id, title, description, thumbnail_url').order('display_order').limit(3);
      if (videosErr) console.error('[Home] videos error:', videosErr);
      setWork(videos || []);

      // Photo crops
      const { data: cropsData, error: cropsErr } = await supabase.from('site_settings').select('value').eq('key', 'photo_crops').single();
      if (cropsErr && cropsErr.code !== 'PGRST116') console.error('[Home] crops error:', cropsErr);
      if (cropsData?.value) setCrops(JSON.parse(cropsData.value));

      // Brand crops
      const { data: brandCropsData } = await supabase.from('site_settings').select('value').eq('key', 'brand_crops').single();
      if (brandCropsData?.value) setBrandCrops(JSON.parse(brandCropsData.value));

      // Page copy — merge DB values over defaults in one shot to avoid FOUC
      const pageCopy = await fetchPageCopy('home');
      setCopy({ ...HOME_DEFAULTS, ...pageCopy });
    }
    load().catch(err => console.error('[Home] load error:', err));
  }, []);

  // Cycle hero photos
  useEffect(() => {
    if (photos.length < 2) return;
    const t = setInterval(() => setPhotoIndex(i => (i + 1) % photos.length), 6000);
    return () => clearInterval(t);
  }, [photos]);

  const NAV_H = 56;

  const c = copy || HOME_DEFAULTS;

  return (
    <div className="page-fade">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section style={{
        position: 'relative',
        height: `calc(100vh - ${NAV_H}px)`,
        marginTop: NAV_H,
        overflow: 'hidden',
        background: 'var(--ink)',
        display: 'flex', alignItems: 'flex-end',
      }}>

        {/* Background images */}
        {photos.map((p, i) => {
          const crop = crops[p.id] || { x: 50, y: 50, scale: 1 };
          return (
            <img
              key={p.id}
              src={p.url}
              alt=""
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: `${crop.x}% ${crop.y}%`,
                transform: `scale(${crop.scale})`,
                transformOrigin: `${crop.x}% ${crop.y}%`,
                opacity: i === photoIndex ? 0.55 : 0,
                transition: 'opacity 2s ease',
                pointerEvents: 'none',
              }}
            />
          );
        })}

        {/* Placeholder video — uncomment and add src when reel is ready */}
        {/* <video
          autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }}
        >
          <source src="/showreel.mp4" type="video/mp4" />
        </video> */}

        {/* Gradient overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(26,24,20,0.85) 0%, rgba(26,24,20,0.2) 60%, transparent 100%)',
        }} />

        {/* Hero text */}
        <div style={{
          position: 'relative', zIndex: 1,
          padding: isMobile ? '3rem 1.5rem' : '4rem 3rem',
          maxWidth: '900px',
          opacity: copy ? 1 : 0,
          transition: 'opacity 300ms ease',
        }}>
          <p style={{
            
            
            fontSize: isMobile ? '28px' : '52px',
            lineHeight: 1.2,
            color: 'white',
            marginBottom: '1.5rem',
          }}>
            {c.hero_headline.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
            ))}
          </p>
          <p style={{
            
            fontSize: isMobile ? '12px' : '13px',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.55)',
            letterSpacing: '0.04em',
            maxWidth: '420px',
            lineHeight: 1.7,
          }}>
            {c.hero_subtext}
          </p>
        </div>
      </section>

      {/* ── Statement ────────────────────────────────────────────────── */}
      <section style={{
        padding: isMobile ? '4rem 1.5rem' : '7rem 3rem',
        borderBottom: '1px solid var(--rule)',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '2rem' : '6rem',
          maxWidth: '1100px',
          margin: '0 auto',
        }}>
          <p style={{
            
            
            fontSize: isMobile ? '24px' : '34px',
            lineHeight: 1.35,
            color: 'var(--ink)',
          }}>
            {c.statement_headline}
          </p>
          <p style={{
            
            fontSize: '14px',
            fontWeight: 300,
            color: 'var(--ink-mid)',
            lineHeight: 1.85,
            alignSelf: 'center',
          }}>
            {c.statement_body}
          </p>
        </div>
      </section>

      {/* ── Selected Work ────────────────────────────────────────────── */}
      <section style={{
        padding: isMobile ? '4rem 1.5rem' : '7rem 3rem',
        borderBottom: '1px solid var(--rule)',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: isMobile ? '2rem' : '3rem' }}>
            <p style={{  fontSize: '11px', fontWeight: 300, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>
              {c.section_work}
            </p>
            <Link to="/work" style={{  fontSize: '12px', fontWeight: 300, color: 'var(--ink-faint)', borderBottom: '1px solid var(--rule)', paddingBottom: '1px' }}>
              View all →
            </Link>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '2.5rem' : '2rem',
          }}>
            {(work.length > 0 ? work : placeholderWork).map((item, i) => (
              <Link to="/work" key={item.id || i} style={{ display: 'block' }}>
                <div style={{ aspectRatio: '16/9', background: 'var(--ink)', marginBottom: '1rem', overflow: 'hidden', position: 'relative' }}>
                  {item.thumbnail_url ? (
                    <img src={item.thumbnail_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 600ms ease' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '10px', fontWeight: 300, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>FILM</span>
                    </div>
                  )}
                </div>
                <p style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink)', marginBottom: '0.35rem', lineHeight: 1.3 }}>
                  {item.title}
                </p>
                {item.description && (
                  <p style={{ fontSize: '12px', fontWeight: 300, color: 'var(--ink-faint)', lineHeight: 1.6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {item.description}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Brands ───────────────────────────────────────────────────── */}
      <section style={{
        padding: isMobile ? '4rem 1.5rem' : '6rem 3rem',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{  fontSize: '11px', fontWeight: 300, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: isMobile ? '2rem' : '3rem' }}>
            {c.section_brands}
          </p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: isMobile ? '1.5rem 2.5rem' : '2rem 3.5rem',
            alignItems: 'center',
          }}>
            {(brands.length > 0 ? brands : placeholderBrands).map((b, i) => {
              const crop = brandCrops[b.id] || { x: 50, y: 50, scale: 1 };
              return b.logo_url ? (
                <div key={b.id || i} style={{ width: '100px', height: '28px', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                  <img src={b.logo_url} alt={b.name} style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    objectFit: 'contain',
                    objectPosition: `${crop.x}% ${crop.y}%`,
                    transform: `scale(${crop.scale})`,
                    transformOrigin: `${crop.x}% ${crop.y}%`,
                  }} />
                </div>
              ) : (
                <span key={b.id || i} style={{ fontSize: '12px', fontWeight: 300, color: 'var(--ink-faint)', letterSpacing: '0.04em' }}>
                  {b.name}
                </span>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
}

const placeholderWork = [
  { title: 'Brand Film',        description: 'A campaign film for a leading Irish brand.' },
  { title: 'Product Launch',    description: 'Launch content across TV and digital platforms.' },
  { title: 'Documentary Short', description: 'A short documentary for a global audience.' },
];

const placeholderBrands = [
  { name: 'Guinness' }, { name: 'Ryanair' }, { name: 'Allianz' },
  { name: 'Circle K' }, { name: 'Universal' }, { name: 'Avonmore' },
];
